import React, { useState } from 'react';
import { Play, ExternalLink, Youtube } from 'lucide-react';

const VideoSection = () => {
  const [selectedVideo, setSelectedVideo] = useState(null);

  const videos = [
    {
      id: 1,
      title: "How to Shop on Our Website - Complete Tutorial",
      description: "Learn how to navigate our website, browse products, add items to cart, and complete your purchase with this comprehensive shopping tutorial.",
      youtubeId: "L_8o7VTXusI",
      thumbnail: `https://img.youtube.com/vi/L_8o7VTXusI/maxresdefault.jpg`,
      category: "Tutorial",
      duration: "Tutorial Video",
      type: "tutorial"
    },
    {
      id: 2,
      title: "Product Demonstration - Water Pump in Action",
      description: "Watch our high-quality water pump in action. See the performance, flow rate, and reliability of our electronic components.",
      youtubeId: "qXv-TvTnEPA",
      thumbnail: `https://img.youtube.com/vi/qXv-TvTnEPA/maxresdefault.jpg`,
      category: "Product Demo",
      duration: "Short Demo",
      type: "demo"
    },
    {
      id: 3,
      title: "Project in Action - Marketing Showcase",
      description: "See our electronic components being used in real-world projects. From concept to completion, watch how our products bring ideas to life.",
      youtubeId: "G6loRTcqWxI",
      thumbnail: `https://img.youtube.com/vi/G6loRTcqWxI/maxresdefault.jpg`,
      category: "Project Showcase",
      duration: "Marketing Video",
      type: "showcase"
    }
  ];

  const openVideo = (video) => {
    setSelectedVideo(video);
  };

  const closeVideo = () => {
    setSelectedVideo(null);
  };

  const getVideoUrl = (youtubeId) => {
    return `https://www.youtube.com/watch?v=${youtubeId}`;
  };

  const getEmbedUrl = (youtubeId) => {
    return `https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0`;
  };

  return (
    <section id="video-section" className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center mb-4">
            <Youtube className="text-red-600 mr-3" size={32} />
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Video Gallery
            </h2>
          </div>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Explore our video content featuring tutorials, product demonstrations, and project showcases. 
            Learn how to use our products and see them in action.
          </p>
        </div>

        {/* Video Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {videos.map((video) => (
            <div
              key={video.id}
              className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
            >
              {/* Video Thumbnail */}
              <div className="relative group cursor-pointer" onClick={() => openVideo(video)}>
                <div className="aspect-video relative overflow-hidden">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      e.target.src = '/api/placeholder/480/270';
                    }}
                  />
                  {/* Play Button Overlay */}
                  <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="bg-red-600 rounded-full p-4 transform scale-100 group-hover:scale-110 transition-transform duration-300">
                      <Play className="text-white" size={24} fill="currentColor" />
                    </div>
                  </div>
                  {/* Category Badge */}
                  <div className="absolute top-3 left-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      video.type === 'tutorial' ? 'bg-blue-500 text-white' :
                      video.type === 'demo' ? 'bg-green-500 text-white' :
                      'bg-purple-500 text-white'
                    }`}>
                      {video.category}
                    </span>
                  </div>
                  {/* Duration Badge */}
                  <div className="absolute bottom-3 right-3">
                    <span className="bg-black bg-opacity-75 text-white px-2 py-1 rounded text-xs">
                      {video.duration}
                    </span>
                  </div>
                </div>
              </div>

              {/* Video Info */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">
                  {video.title}
                </h3>
                <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                  {video.description}
                </p>
                
                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={() => openVideo(video)}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors duration-300 flex items-center justify-center"
                  >
                    <Play size={16} className="mr-2" />
                    Watch Now
                  </button>
                  <a
                    href={getVideoUrl(video.youtubeId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-2 px-4 rounded-lg transition-colors duration-300 flex items-center justify-center"
                  >
                    <ExternalLink size={16} />
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Video Modal */}
        {selectedVideo && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
              {/* Modal Header */}
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-xl font-bold text-gray-900 truncate pr-4">
                  {selectedVideo.title}
                </h3>
                <button
                  onClick={closeVideo}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                >
                  ×
                </button>
              </div>

              {/* Video Player */}
              <div className="aspect-video">
                <iframe
                  src={getEmbedUrl(selectedVideo.youtubeId)}
                  title={selectedVideo.title}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>

              {/* Video Description */}
              <div className="p-4">
                <p className="text-gray-600">
                  {selectedVideo.description}
                </p>
                <div className="mt-4 flex justify-between items-center">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    selectedVideo.type === 'tutorial' ? 'bg-blue-100 text-blue-800' :
                    selectedVideo.type === 'demo' ? 'bg-green-100 text-green-800' :
                    'bg-purple-100 text-purple-800'
                  }`}>
                    {selectedVideo.category}
                  </span>
                  <a
                    href={getVideoUrl(selectedVideo.youtubeId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-red-600 hover:text-red-700 font-semibold flex items-center"
                  >
                    <Youtube size={16} className="mr-1" />
                    Watch on YouTube
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Call to Action */}
        <div className="text-center mt-16">
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Want to See More?
            </h3>
            <p className="text-gray-600 mb-6">
              Subscribe to our YouTube channel for the latest tutorials, product demos, and project showcases.
            </p>
            <a
              href="https://youtube.com/@giftedsolutions"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-300"
            >
              <Youtube size={20} className="mr-2" />
              Subscribe to Our Channel
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VideoSection;
